// iTunes Search & Lookup API client.
//
// Uses normal fetch() per the CORS decision in docs/PLAN.md. The iTunes API
// returns permissive CORS headers in practice, so JSONP (src/api/jsonp.js) is
// kept as a fallback only and is not wired in here.
import { jsonp } from './jsonp.js';
import { searchLangForCountry } from '../data/storefronts.js';
const SEARCH_URL = 'https://itunes.apple.com/search';
const LOOKUP_URL = 'https://itunes.apple.com/lookup';

// Set to true only if a real browser CORS test proves fetch() is blocked.
const USE_JSONP = false;

function buildUrl(base, params) {
  const url = new URL(base);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function requestJson(url) {
  if (USE_JSONP) {
    return jsonp(url);
  }
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`iTunes request failed with HTTP ${res.status}`);
  }
  // iTunes occasionally serves JSON with a text/javascript content type, so
  // parse the body as text and JSON.parse it rather than trusting res.json().
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('iTunes returned a non-JSON response');
  }
}

function normalizeSearchLang(lang) {
  const normalized = String(lang ?? '').trim().toLowerCase().replace(/-/g, '_');
  return /^[a-z]{2,3}_[a-z]{2}$/.test(normalized) ? normalized : null;
}

/**
 * Search the iTunes Search API for software matching a query.
 * @param {string} query
 * @param {string} [country='us']
 * @param {number} [limit=1]
 * @param {string|null} [genreId=null] - Optional Apple genre ID to restrict results (e.g. '6014' for Games)
 * @param {'ios'|'mac'} [platform='ios'] - Target platform: 'ios' for iPhone/iPad, 'mac' for macOS
 * @param {string|null} [lang] - Optional iTunes Search API language code (e.g. 'ja_jp'); defaults from storefront metadata
 * @returns {Promise<object|null>} The first software result, or null if none.
 */
export async function searchApps(query, country = 'us', limit = 1, genreId = null, platform = 'ios', lang = undefined) {
  const trimmed = (query ?? '').trim();
  if (!trimmed) return null;

  const entity = platform === 'mac' ? 'macSoftware' : 'software';
  const searchLang = normalizeSearchLang(lang) ?? searchLangForCountry(country);
  const url = buildUrl(SEARCH_URL, {
    term: trimmed,
    entity,
    limit,
    country,
    lang: searchLang,
    ...(genreId ? { genreId } : {}),
  });
  const data = await requestJson(url);
  if (!data || !Array.isArray(data.results) || data.results.length === 0) {
    return null;
  }
  return data.results[0];
}

/**
 * Look up an app by trackId in a specific storefront.
 * @param {number|string} trackId
 * @param {string} country
 * @returns {Promise<object>} The raw iTunes Lookup response ({ resultCount, results }).
 */
export async function lookupAppByCountry(trackId, country) {
  const url = buildUrl(LOOKUP_URL, { id: trackId, country });
  return requestJson(url);
}

/**
 * Resolve an app's summary metadata by its trackId. Used for shared links
 * (`?appId=...`) where we have an ID rather than a search query.
 * @param {number|string} appId
 * @param {string} [country='us']
 * @returns {Promise<object|null>} The software result, or null if not found.
 */
export async function lookupAppById(appId, country = 'us') {
  const id = String(appId ?? '').trim();
  if (!id) return null;
  const data = await lookupAppByCountry(id, country);
  if (!data || !Array.isArray(data.results) || data.results.length === 0) {
    return null;
  }
  return data.results[0];
}

/**
 * Fetch other software apps by the same developer (artist) using the artistId.
 * Returns up to `limit` software apps. Excludes the source app (by excludeTrackId)
 * so it doesn't appear in the list.
 *
 * @param {number|string} artistId
 * @param {string} [country='us']
 * @param {number} [limit=10]
 * @param {number|string|null} [excludeTrackId=null]
 * @returns {Promise<object[]>} Array of raw iTunes software results.
 */
export async function lookupDeveloperApps(artistId, country = 'us', limit = 10, excludeTrackId = null) {
  const id = String(artistId ?? '').trim();
  if (!id) return [];
  const url = buildUrl(LOOKUP_URL, { id, entity: 'software', country, limit: limit + 1 });
  const data = await requestJson(url);
  if (!data || !Array.isArray(data.results)) return [];
  // The first result is the artist/developer record — filter to wrapperType track + kind software
  return data.results
    .filter((r) => r.wrapperType === 'track' && r.kind === 'software')
    .filter((r) => excludeTrackId == null || String(r.trackId) !== String(excludeTrackId))
    .slice(0, limit);
}
